import { Button, Col, Grid, Row, Spinner } from 'gu-ui-kit'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Form, reduxForm } from 'redux-form'
import { useHistory } from 'react-router'
import * as Yup from 'yup'
import validator from 'utils/asyncValidator'
import formatMoney, { formatToString } from 'utils/formatMoney'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import useQueryParams from 'utils/hooks/useQueryParams'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import {
  Accounts,
  Amount,
  Credited,
  ExchangeRate,
  Total,
  Wallets,
} from 'modules/Forms/Billing/subcomponents'
import { useBilling } from 'modules/Forms/Billing/useBilling'
import styles from 'modules/Forms/Billing/Billing-forms.module.scss'
import { notificationShow } from 'modules/Notifications/actions'
import EmptyWalletNotification from 'modules/Forms/Billing/subcomponents/EmptyWalletNotification'

const validationSchema = Yup.object().shape({
  method: Yup.string().required('billing::errors.choose_payment_method'),
  payment_amount: Yup.object().shape({
    value: Yup.number()
      .typeError('billing::errors.required_field')
      .test({
        name: 'min_amount',
        test: function (value, ctx) {
          const { path, parent } = this
          const { min_amount = 0 } = ctx.from?.[1]?.value ?? {}

          if (!value) {
            return false
          }

          if (+value >= min_amount) {
            return true
          }

          return ctx.createError({
            path,
            message: i18n.t('billing::errors.specify_amount', {
              amount: formatToString(min_amount, parent.currency),
            }),
          })
        },
      })
      .required('billing::errors.required_field'),
    currency: Yup.string(),
  }),
  wallet_id: Yup.string()
    .when('is_wallets', {
      is: true,
      then: (schema) => schema.required('billing::errors.required_field'),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
  min_amount: Yup.number(),
})

const CALCULATION_DELAY_TIME = 1000

let AccountsBillingForm = ({
  handleSubmit,
  form,
  reset,
  autofill,
  accountId = null,
  onClose = () => {},
  networkId = null,
  queryAccounts = [],
}) => {
  const { t } = useTranslation(['billing'])
  const dispatch = useDispatch()
  const { query, setQuery } = useQueryParams()
  const [formInitiated, setFormInitiated] = useState(false)
  const history = useHistory()
  const [redirectUrl, setRedirectUrl] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [summary, setSummary] = useState(null)
  const [topUpAccountsCompleted, setTopUpAccountsCompleted] = useState(false)

  useEffect(() => {
    setAccounts(queryAccounts)
  }, [queryAccounts])

  const {
    data: {
      activeMethod,
      accountData: { loaded: accountDataLoaded, loading: accountDataLoading },
    } = {},
    calculate,
    topUp,
    values = {},
  } = useBilling(form)

  const [billingConfig, setBillingConfig] = useLocalStorage(form, {})

  const {
    payment_amount: { value: amount = null, currency = null } = {},
    payment_profile_id = null,
    method = null,
    is_wallets = false,
    wallet_id = null,
    min_amount = 0,
  } = values

  useEffect(() => {
    const { value: payment_value = null } =
      summary?.calculated?.deposit?.payment ?? {}
    if (payment_value && +amount !== payment_value) {
      autofill('amount', payment_value)
    }
  }, [summary])

  const updateAccounts = useCallback(
    ({ accountId, update }) =>
      setAccounts((accounts) =>
        accounts.map((account) =>
          +account.id === +accountId ? { ...account, ...update } : account,
        ),
      ),
    [],
  )

  const submit = useCallback(
    (values) => {
      const processes = []
      setTopUpAccountsCompleted(false)

      accounts.map(({ id: accountId, type: networkType }) => {
        updateAccounts({ accountId, update: { status: 'loading' } })

        const process = topUp({
          ...values,
          accountId,
          networkType,
          formName: form,
          isMultipleAccounts: true,
        })
          .then(
            (response) =>
              response.status === 200 &&
              updateAccounts({ accountId, update: { status: 'success' } }),
          )
          .catch((error) =>
            updateAccounts({
              accountId,
              update: { status: 'failure', error },
            }),
          )

        processes.push(process)
      })

      Promise.all(processes).finally(() => {
        setTopUpAccountsCompleted(true)
        const { method, wallet_id } = values
        setBillingConfig({ method, wallet_id })
      })
    },
    [accounts, form, values],
  )

  useEffect(() => {
    if (!topUpAccountsCompleted || !accounts?.length) {
      return
    }

    const successfullyAccounts = accounts.filter(
      ({ status }) => status === 'success',
    )

    const notificationAction =
      successfullyAccounts.length > 0
        ? {
            type: 'success',
            message: t('accounts_replenish.notifications.replenish.completed', {
              amount: formatMoney(
                successfullyAccounts.reduce(
                  (acc, { calculated = {} }) =>
                    acc + calculated?.transfer?.account?.value ?? 0,
                  0,
                ),
                successfullyAccounts[0].calculated.transfer?.account?.currency,
              ),
            }),
          }
        : {
            type: 'error',
            message: t('accounts_replenish.notifications.replenish.failure'),
          }

    dispatch(notificationShow(notificationAction))
  }, [topUpAccountsCompleted, accounts])

  useEffect(() => {
    global.getBack = () => {
      reset()
      redirectUrl && history.push(redirectUrl)

      dispatch(
        notificationShow({
          type: 'success',
          message: t('accounts_replenish.notifications.successful_replenish'),
        }),
      )

      global.getBack = undefined
      setRedirectUrl(null)
    }
  }, [redirectUrl])

  const initForm = useCallback(() => {
    const { method = null, wallet_id = null } = billingConfig // сохраненные в localstorage данные

    const activeMethodName = activeMethod.method
    const isCurrentBillingConfig = method === activeMethodName
    const isCurrentWallet =
      isCurrentBillingConfig &&
      activeMethod?.params?.wallets?.findIndex(({ id }) => id === wallet_id) >
        -1

    const isWallets = !!activeMethod?.params?.wallets
    const firstWalletId = activeMethod?.params?.wallets?.[0]?.id

    const walletId = isWallets
      ? firstWalletId === null
        ? 'null'
        : firstWalletId
      : null

    autofill('method', activeMethodName)
    autofill('is_wallets', !!activeMethod?.params?.wallets)
    autofill('wallet_id', isCurrentWallet ? wallet_id : walletId)

    setFormInitiated(true)
  }, [activeMethod])

  useEffect(() => {
    if (activeMethod) {
      initForm()
    } else {
      reset()
    }
  }, [activeMethod])

  useEffect(() => {
    if (activeMethod) {
      let accounts_ids =
        accounts.length > 0 ? { ids: accounts.map(({ id }) => id) } : {}
      setQuery({ ...query, method: activeMethod.method, ...accounts_ids })
    }
  }, [accounts, activeMethod])

  useEffect(() => {
    if (activeMethod && accountDataLoaded) {
      initForm()
    }
  }, [activeMethod, accountDataLoaded])

  useEffect(() => {
    if (!currency || !activeMethod) {
      return
    }

    const { min_amounts_map = null, min_amounts = null } = activeMethod
    let min_amount = min_amounts?.[currency] ?? 0

    if (min_amounts_map?.length) {
      min_amount =
        min_amounts_map.find(
          (minAmount) =>
            ((payment_profile_id !== null &&
              minAmount?.payment_profile_id === payment_profile_id) ||
              (wallet_id !== null && minAmount?.wallet_id === wallet_id)) &&
            minAmount?.currency === currency,
        )?.value ?? 0
    }

    autofill('min_amount', min_amount)
  }, [currency, activeMethod, payment_profile_id, wallet_id])

  useEffect(() => autofill('payment_amount.value', amount), [min_amount])

  const isFormReady = useMemo(
    () =>
      amount &&
      method &&
      is_wallets &&
      wallet_id &&
      accounts.length &&
      currency &&
      +amount >= +values.min_amount,

    [values],
  )

  const resetCalculatedData = useCallback(() => {
    accounts.forEach(({ id: accountId }) =>
      updateAccounts({ accountId, update: { calculated: {} } }),
    )
  }, [accounts])

  const runCalculate = useCallback(() => {
    calculate({
      networkId,
      accountId,
      formName: form,
      ...values,
      accounts: accounts.map(({ id }) => id),
      delayTime: CALCULATION_DELAY_TIME,
    }).then(({ data: accounts = {}, summary }) => {
      setSummary(summary)
      Object.entries(accounts).forEach(([accountId, update]) =>
        updateAccounts({ accountId, update: { calculated: update } }),
      )
    })
  }, [values, networkId, accounts])

  // список кошельков
  const wallets = useMemo(
    () => activeMethod?.params?.wallets ?? null,
    [formInitiated, activeMethod, accountDataLoaded],
  )

  // кошелек
  const wallet = useMemo(() => {
    if (wallet_id) {
      if (activeMethod?.method === 'user_referral_balance') {
        return activeMethod?.params.wallets?.[0]
      }

      return wallets?.find(({ id }) => id === wallet_id) || false
    }

    return false
  }, [formInitiated, wallet_id, wallets, accountDataLoaded, activeMethod])

  // спец валюта от способа пополнения

  // список валют
  // если нет спец валют используем валюты из списка валют
  // если нет ни того ни другого вообще не используем список
  const currencies = useMemo(
    () => (wallet?.currency ? null : activeMethod?.currencies),
    [formInitiated, activeMethod, wallet?.currency],
  )

  const currentCurrency = useMemo(
    () => wallet?.currency || currencies?.[0]?.currency || null,
    [currencies, wallet?.currency, formInitiated],
  )

  useEffect(() => {
    autofill('payment_amount.currency', currentCurrency)
  }, [currentCurrency, currencies, activeMethod])

  useEffect(
    () => calculate.finished && !isFormReady && resetCalculatedData(),
    [isFormReady, calculate.finished],
  )

  const handleRemoveAccount = useCallback(
    (id) =>
      setAccounts((accounts) =>
        accounts.filter(({ id: accountId }) => id !== accountId),
      ),
    [],
  )

  //more code duplication for god of duplication! but not the same :)
  const isAmountDisabled = useMemo(() => {
    switch (true) {
      case is_wallets && (!wallet || wallet?.balance === 0):
        return true
      default:
        return false
    }
  }, [wallet, is_wallets])

  useEffect(() => {
    if (!isFormReady) {
      return
    }

    resetCalculatedData()
    !isAmountDisabled && runCalculate()
  }, [isFormReady, values, accounts.length])

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <EmptyWalletNotification activeMethod={activeMethod} wallet={wallet} />

      <Wallets
        walletsLoading={accountDataLoading}
        className={styles['billing__row']}
        disabled={topUpAccountsCompleted}
        wallets={wallets}
        t={t}
      />

      <Amount
        name={'payment_amount.value'}
        currencyName={'payment_amount.currency'}
        className={styles['billing__row']}
        currencies={currencies}
        currency={currency}
        disabled={topUpAccountsCompleted || isAmountDisabled}
        label={t('amount_each_account')}
        t={t}
      />

      <Accounts
        className={styles['billing__row']}
        accounts={accounts}
        t={t}
        currency={currentCurrency}
        handleRemoveAccount={handleRemoveAccount}
        topUpAccountsCompleted={topUpAccountsCompleted}
      />

      {calculate.finished && (
        <div className={styles['billing__deposit']}>
          <div className={styles['billing__amount-rounding']}>
            <Grid>
              {accountId && (
                <Row alignItems="center" justifyContent="between">
                  <Col
                    s={{ span: 12, order: 1 }}
                    xs={{ span: 12, order: 1 }}
                    sm={{ span: 'auto', order: 1 }}
                  >
                    {/* Будет зачислено */}
                    <Credited {...summary} />
                  </Col>
                </Row>
              )}
            </Grid>
          </div>
          {!topUpAccountsCompleted && (
            <div className={styles['billing__calculated-data']}>
              <Grid>
                <Row>
                  <Col
                    s={{ span: 12, order: 1 }}
                    xs={{ span: 12, order: 1 }}
                    sm={{ span: 12, order: 1 }}
                    lg={{ span: 12, order: 2 }}
                    className={styles['billing__total-with-commission']}
                  >
                    {/* Итоговая сумма */}
                    <Total {...summary} />
                    <span
                      className={styles['billing__total-without-commission']}
                    >
                      <ExchangeRate {...summary} />
                    </span>
                  </Col>
                </Row>
              </Grid>
            </div>
          )}

          {calculate.progress && (
            <div className={styles['billing__deposit-spinner']}>
              <Spinner color="Label-GU-Primary" />
            </div>
          )}
        </div>
      )}

      <div className={styles['billing__buttons']}>
        <Grid gap={[16, 0]}>
          {topUpAccountsCompleted ? (
            <Col s={12}>
              <Button
                className={styles['billing__button']}
                type="button"
                fullWidth
                size="l"
                onClick={onClose}
                variant="primary"
              >
                {t('accounts_replenish.back_to_accounts')}
              </Button>
            </Col>
          ) : (
            <>
              <Col
                className={styles['billing__buttons-reset']}
                s={{
                  span: 12,
                  order: 3,
                }}
                xs={{
                  span: 12,
                  order: 3,
                }}
                sm={{
                  span: 5,
                  order: 1,
                }}
              >
                <Button
                  className={styles['billing__button']}
                  size="l"
                  onClick={onClose}
                  variant={'ghost'}
                  grey
                >
                  {t('common::form_buttons.cancel')}
                </Button>
              </Col>
              <Col
                s={{
                  span: 12,
                  order: 1,
                }}
                xs={{
                  span: 12,
                  order: 1,
                }}
                sm={{
                  span: 7,
                  order: 2,
                }}
              >
                <Button
                  className={styles['billing__button']}
                  bgColor={calculate.finished ? 'Label-GU-Primary' : 'none'}
                  type="submit"
                  size="l"
                  disabled={
                    !isFormReady ||
                    !calculate.finished ||
                    calculate.progress ||
                    topUp.progress ||
                    !accounts.length ||
                    !summary
                  }
                  variant="primary"
                >
                  {calculate.progress || topUp.progress
                    ? t('loading')
                    : t('pay')}
                </Button>
              </Col>
            </>
          )}
        </Grid>
      </div>
    </Form>
  )
}

AccountsBillingForm.propTypes = {
  handleSubmit: PropTypes.func,
  form: PropTypes.string,
  reset: PropTypes.func,
  accountId: PropTypes.string,
  autofill: PropTypes.func,
  focus: PropTypes.func,
  asyncValidate: PropTypes.func,
  blur: PropTypes.func,
  onClose: PropTypes.func,
  dirty: PropTypes.bool,
  valid: PropTypes.bool,
  networkId: PropTypes.string,
  queryAccounts: PropTypes.array,
}

const AccountsBillingFormRedux = reduxForm({
  form: 'accounts-billing',
  enableReinitialize: true,
  shouldAsyncValidate,
  touchOnChange: true,
  asyncValidate: validator(validationSchema),
})(AccountsBillingForm)

export default AccountsBillingFormRedux
